import React, { useState, useMemo } from 'react';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Collapse, Tooltip, Divider
} from '@mui/material';
import {
  DashboardOutlined,
  GavelOutlined,
  MenuBookOutlined,
  ManageAccountsOutlined,
  AdminPanelSettingsOutlined,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  ShieldOutlined,
  BusinessOutlined,
  ReportProblemOutlined,
  AssessmentOutlined,
  FactCheckOutlined,
  LibraryBooksOutlined,
  QuizOutlined,
  AssignmentOutlined,
  ListAltOutlined,
  GroupOutlined,
  GroupWorkOutlined,
  SecurityOutlined,
  HistoryOutlined,
  CategoryOutlined,
  WarningAmberOutlined,
  PolicyOutlined,
  DescriptionOutlined,
  FolderOpenOutlined,
  BallotOutlined,
  LockPersonOutlined,
  ContactPageOutlined,
  FeedbackOutlined,
  AutoAwesome,
  ManageSearch,
  AccountTreeOutlined,
  NotificationsOutlined,
  UpdateOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import octanscareLogo from '../../assets/images/octanscare-logo.png';

const DRAWER_WIDTH = 248;
const DRAWER_WIDTH_COLLAPSED = 60;

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: NavItem[];
  permission?: string;
  role?: string;
  dividerBefore?: boolean;
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    icon: <DashboardOutlined sx={{ fontSize: 20 }} />,
    path: '/dashboard',
  },
  {
    label: 'Knowledge',
    icon: <MenuBookOutlined sx={{ fontSize: 20 }} />,
    children: [
      {
        label: 'Policies',
        icon: <PolicyOutlined sx={{ fontSize: 18 }} />,
        path: '/knowledge/policies',
        permission: 'knowledge.documents.read',
      },
      {
        label: 'Procedures',
        icon: <DescriptionOutlined sx={{ fontSize: 18 }} />,
        path: '/knowledge/procedures',
        permission: 'knowledge.documents.read',
      },
      {
        label: 'Standing Orders',
        icon: <LibraryBooksOutlined sx={{ fontSize: 18 }} />,
        path: '/knowledge/standing-orders',
        permission: 'knowledge.documents.read',
      },
      {
        label: 'Resources',
        icon: <FolderOpenOutlined sx={{ fontSize: 18 }} />,
        path: '/knowledge/resources',
        permission: 'knowledge.documents.read',
      },
      {
        label: 'Standards Watch',
        icon: <UpdateOutlined sx={{ fontSize: 18 }} />,
        path: '/knowledge/standards-watch',
        permission: 'knowledge.standards.read',
      },
      {
        label: 'AI Builder',
        icon: <AutoAwesome sx={{ fontSize: 18 }} />,
        path: '/knowledge/ai-creator',
        permission: 'ai.use',
      },
      {
        label: 'RAG Search',
        icon: <ManageSearch sx={{ fontSize: 18 }} />,
        path: '/knowledge/ai-search',
        permission: 'ai.use',
      },
      {
        label: 'Knowledge Graph',
        icon: <AccountTreeOutlined sx={{ fontSize: 18 }} />,
        path: '/knowledge/ai-graph',
        permission: 'ai.use',
      },
    ],
  },
  {
    label: 'Compliance',
    icon: <GavelOutlined sx={{ fontSize: 20 }} />,
    children: [
      {
        label: 'Audits',
        icon: <FactCheckOutlined sx={{ fontSize: 18 }} />,
        path: '/compliance/audits',
        permission: 'compliance.audits.read',
      },
      {
        label: 'Quizzes',
        icon: <QuizOutlined sx={{ fontSize: 18 }} />,
        path: '/knowledge/quizzes',
        permission: 'knowledge.documents.read',
      },
      {
        label: 'Risk Assessments',
        icon: <AssessmentOutlined sx={{ fontSize: 18 }} />,
        path: '/compliance/risk-assessments',
        permission: 'compliance.risks.read',
      },
      {
        label: 'Risk Register',
        icon: <ListAltOutlined sx={{ fontSize: 18 }} />,
        path: '/compliance/risk-register',
        permission: 'compliance.risks.read',
      },
      {
        label: 'RTAPs',
        icon: <AssignmentOutlined sx={{ fontSize: 18 }} />,
        path: '/compliance/rtaps',
        permission: 'compliance.risks.read',
      },
      {
        label: 'Incidents',
        icon: <ReportProblemOutlined sx={{ fontSize: 18 }} />,
        path: '/compliance/incidents',
        permission: 'compliance.incidents.read',
      },
      {
        label: 'Near Misses',
        icon: <WarningAmberOutlined sx={{ fontSize: 18 }} />,
        path: '/compliance/near-misses',
        permission: 'compliance.incidents.read',
      },
      {
        label: 'NCRs',
        icon: <BallotOutlined sx={{ fontSize: 18 }} />,
        path: '/compliance/ncrs',
        permission: 'compliance.risks.read',
      },
      {
        label: 'Master Register',
        icon: <ListAltOutlined sx={{ fontSize: 18 }} />,
        path: '/compliance/master-register',
        permission: 'compliance.incidents.read',
      },
    ],
  },
  {
    label: 'Privacy',
    icon: <ShieldOutlined sx={{ fontSize: 20 }} />,
    children: [
      {
        label: 'Privacy Incidents',
        icon: <LockPersonOutlined sx={{ fontSize: 18 }} />,
        path: '/privacy/incidents',
        permission: 'privacy.incidents.read',
      },
      {
        label: 'PIAs',
        icon: <SecurityOutlined sx={{ fontSize: 18 }} />,
        path: '/privacy/pias',
        permission: 'privacy.pias.read',
      },
      {
        label: 'SARs',
        icon: <ContactPageOutlined sx={{ fontSize: 18 }} />,
        path: '/privacy/sars',
        permission: 'privacy.sars.read',
      },
    ],
  },
  {
    label: 'Management',
    icon: <ManageAccountsOutlined sx={{ fontSize: 20 }} />,
    children: [
      {
        label: 'Complaints',
        icon: <FeedbackOutlined sx={{ fontSize: 18 }} />,
        path: '/management/complaints',
        permission: 'management.complaints.read',
      },
      {
        label: 'Distribution Groups',
        icon: <GroupWorkOutlined sx={{ fontSize: 18 }} />,
        path: '/management/distribution-groups',
        permission: 'management.distributiongroups.read',
      },
    ],
  },
  {
    label: 'Admin',
    icon: <AdminPanelSettingsOutlined sx={{ fontSize: 20 }} />,
    dividerBefore: true,
    children: [
      {
        label: 'Users',
        icon: <GroupOutlined sx={{ fontSize: 18 }} />,
        path: '/admin/users',
        permission: 'admin.users.read',
      },
      {
        label: 'Roles',
        icon: <SecurityOutlined sx={{ fontSize: 18 }} />,
        path: '/admin/roles',
        permission: 'admin.roles.read',
      },
      {
        label: 'Organisations',
        icon: <BusinessOutlined sx={{ fontSize: 18 }} />,
        path: '/admin/organisations',
        permission: 'admin.organisations.read',
      },
      {
        label: 'Lookups',
        icon: <CategoryOutlined sx={{ fontSize: 18 }} />,
        path: '/admin/lookups',
        permission: 'admin.lookups.read',
      },
      {
        label: 'Notification Templates',
        icon: <NotificationsOutlined sx={{ fontSize: 18 }} />,
        path: '/admin/notification-templates',
        permission: 'admin.notifications.manage',
      },
      {
        label: 'Audit Logs',
        icon: <HistoryOutlined sx={{ fontSize: 18 }} />,
        path: '/admin/audit-logs',
        permission: 'admin.auditlogs.read',
      },
    ],
  },
];

export const Sidebar: React.FC<{ collapsed?: boolean }> = ({ collapsed = false }) => {
  const { hasPermission, hasRole } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  const [openSections, setOpenSections] = useState<string[]>([]);

  const isChildActive = (path?: string): boolean =>
    Boolean(path && location.pathname.startsWith(path.split('/:')[0]));

  const isActive = (path?: string): boolean =>
    Boolean(path && location.pathname === path);

  const isParentActive = (item: NavItem): boolean =>
    Boolean(item.children?.some(c => isChildActive(c.path)));

  const toggleSection = (label: string) => {
    setOpenSections(prev =>
      prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
    );
  };

  const filteredNav = useMemo(() => {
    return navItems.filter(item => {
      if (item.role && !hasRole(item.role)) return false;
      if (item.permission && !hasPermission(item.permission)) return false;
      if (item.children) {
        const visibleChildren = item.children.filter(
          child => !child.permission || hasPermission(child.permission)
        );
        return visibleChildren.length > 0;
      }
      return true;
    });
  }, [hasPermission, hasRole]);

  const drawerWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        transition: 'width 0.2s ease',
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          overflowX: 'hidden',
          transition: 'width 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '4px 0 20px rgba(18, 38, 63, 0.03)',
        },
      }}
    >
      {/* Logo / Brand */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: collapsed ? 0 : 2,
          py: 0,
          height: 66,
          borderBottom: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          flexShrink: 0,
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #003366 0%, #00A6C2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Box component="img" src={octanscareLogo} alt="Octans GRC" sx={{ width: 40, height: 40, objectFit: 'contain' }} />
        </Box>
        {!collapsed && (
          <Box sx={{ ml: 1.5, overflow: 'hidden', flex: 1 }}>
            <Typography
              sx={{
                color: 'text.primary',
                fontWeight: 700,
                fontSize: '0.9rem',
                letterSpacing: 0,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}
            >
              Octans GRC
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
              Open the Door to Digital Care
            </Typography>
          </Box>
        )}
      </Box>

      {/* Navigation */}
      <List sx={{ p: 1, pt: 1, flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {filteredNav.map((item) => (
          <React.Fragment key={item.label}>
            {item.dividerBefore && (
              <>
                {!collapsed && (
                  <Typography sx={{ px: 1.5, pt: 1.5, pb: 0.75, fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.12em', color: '#4B5F73', textTransform: 'uppercase' }}>
                    Admin
                  </Typography>
                )}
                {collapsed && <Divider sx={{ borderColor: 'divider', my: 1 }} />}
              </>
            )}

            {item.children ? (
              <>
                <Tooltip title={collapsed ? item.label : ''} placement="right" arrow>
                  <ListItemButton
                    onClick={() => !collapsed && toggleSection(item.label)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.35,
                      px: collapsed ? 0 : 1.5,
                      py: 0.75,
                      minHeight: 36,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      color: isParentActive(item) ? '#ffffff' : 'text.secondary',
                      '&:hover': { bgcolor: theme => isParentActive(item) ? 'primary.main' : theme.palette.mode === 'dark' ? '#13243a' : '#EAF5F7' },
                      ...(isParentActive(item) ? { bgcolor: 'primary.main', boxShadow: '0 8px 18px rgba(36, 82, 199, 0.18)' } : {}),
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: collapsed ? 0 : 34,
                        color: isParentActive(item) ? '#ffffff' : 'text.secondary',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!collapsed && (
                      <>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: '0.78rem',
                            fontWeight: isParentActive(item) ? 700 : 600,
                            color: isParentActive(item) ? '#ffffff' : 'text.primary',
                          }}
                        />
                        {openSections.includes(item.label)
                          ? <ExpandLessOutlined sx={{ fontSize: 16, color: isParentActive(item) ? '#ffffff' : 'text.secondary' }} />
                          : <ExpandMoreOutlined sx={{ fontSize: 16, color: isParentActive(item) ? '#ffffff' : 'text.secondary' }} />
                        }
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>

                {!collapsed && (
                  <Collapse in={openSections.includes(item.label)} timeout="auto" unmountOnExit>
                    <List disablePadding sx={{ mb: 0.5 }}>
                      {item.children
                        .filter(child => !child.permission || hasPermission(child.permission))
                        .map(child => {
                          const active = isChildActive(child.path);
                          return (
                            <ListItemButton
                              key={child.path}
                              onClick={() => navigate(child.path!)}
                              selected={active}
                              sx={{
                                borderRadius: 2,
                                mb: 0.25,
                                pl: 4.5,
                                pr: 1.5,
                                py: 0.5,
                                minHeight: 34,
                                '&.Mui-selected': {
                                  bgcolor: '#EAF5F7',
                                  '&:hover': { bgcolor: '#EAF5F7' },
                                },
                                '&:hover': { bgcolor: theme => theme.palette.mode === 'dark' ? '#13243a' : '#f6faff' },
                              }}
                            >
                              <ListItemIcon
                                sx={{ minWidth: 28, color: active ? 'primary.main' : 'text.secondary' }}
                              >
                                {child.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={child.label}
                                primaryTypographyProps={{
                                  fontSize: '0.76rem',
                                  fontWeight: active ? 700 : 500,
                                  color: active ? 'primary.main' : 'text.secondary',
                                }}
                              />
                            </ListItemButton>
                          );
                        })}
                    </List>
                  </Collapse>
                )}
              </>
            ) : (
              <Tooltip title={collapsed ? item.label : ''} placement="right" arrow>
                <ListItemButton
                  onClick={() => navigate(item.path!)}
                  selected={isActive(item.path)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.35,
                    px: collapsed ? 0 : 1.5,
                    py: 0.75,
                    minHeight: 36,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: '#ffffff',
                      boxShadow: '0 8px 18px rgba(36, 82, 199, 0.18)',
                      '&:hover': { bgcolor: 'primary.dark' },
                    },
                    '&:hover': { bgcolor: theme => theme.palette.mode === 'dark' ? '#13243a' : '#EAF5F7' },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 34,
                      color: isActive(item.path) ? '#ffffff' : 'text.secondary',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.78rem',
                        fontWeight: isActive(item.path) ? 700 : 600,
                        color: isActive(item.path) ? '#ffffff' : 'text.primary',
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            )}
          </React.Fragment>
        ))}
      </List>

    </Drawer>
  );
};

export default Sidebar;
